import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';
import { CSS } from '@dnd-kit/utilities';
import { vars } from '../../../theme';
import spacing from '../../../styles/spacing';
import { useSortable } from '@dnd-kit/sortable';

import { KanbanCard } from '../HBKanban.types';
import HBKanbanCardHeader from './HBKanbanCardHeader/HBKanbanCardHeader';
import { getKanbanCardStyles } from './HBKanbanCard.styles';
import { useHBKanbanContext } from '../HBKanbanContext';
import { HBKanbanCardContent } from './HBKanbanCardContent/HBKanbanCardContent';

const HBKanbanCard = <TItemData = any,>({
    card,
    className,
    isOverlay = false
}: {
    card: KanbanCard<TItemData>;
    className?: string;
    isOverlay?: boolean;
}) => {
    const {
        isCardSelected,
        testIdName,
        isInSelectableState,
        activeIds,
        onToggleSelect,
        draggingOverNonDroppable,
        onCardClick
    } = useHBKanbanContext<TItemData>();
    const selected = onToggleSelect ? isCardSelected(card) : false;

    const isDraggingDisabled =
        onToggleSelect && isInSelectableState && !selected;
    const isAnyCardDragging = (activeIds?.length ?? 0) > 0;

    const cardRef = useRef<HTMLDivElement>(null);

    const {
        setNodeRef,
        attributes,
        listeners,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: card.id,
        disabled: isDraggingDisabled,
        data: {
            type: 'kanban-card',
            card
        },
        attributes: {
            roleDescription: 'Task'
        }
    });

    // Combine sortable ref with card ref using callback ref pattern
    const combinedRef = useCallback(
        (node: HTMLDivElement | null) => {
            setNodeRef(node);
            cardRef.current = node;
        },
        [setNodeRef]
    );

    const customListeners = {
        onClick: (event: React.MouseEvent) => {
            onCardClick?.(card, event);
        }
    };

    // Don't apply transform/transition during drag - cards stay in place
    // Only the DragOverlay shows the floating card
    // Disable transforms for ALL cards when ANY card is being dragged to prevent preview shifting
    const style =
        isDragging || isAnyCardDragging
            ? {}
            : {
                  transition,
                  transform: CSS.Translate.toString(transform)
              };

    return (
        <Card
            ref={combinedRef}
            style={style}
            $selected={selected}
            $isInSelectableState={onToggleSelect ? isInSelectableState : false}
            $isOverlay={isOverlay}
            $isDragging={isDragging}
            $draggingOverNonDroppable={
                isOverlay ? draggingOverNonDroppable !== null : false
            }
            className={className}
            data-testid={
                testIdName
                    ? `${testIdName}-${card.testId ?? card.id}`
                    : undefined
            }
            {...attributes}
            {...listeners}
            {...customListeners}
        >
            <HBKanbanCardHeader card={card} />
            <HBKanbanCardContent
                card={card}
                isOverlay={isOverlay}
                isDragging={isDragging}
                cardRef={cardRef}
            />
        </Card>
    );
};

export default HBKanbanCard;

const Card = styled.div<{
    $selected: boolean;
    $isInSelectableState: boolean;
    $isOverlay: boolean;
    $isDragging: boolean;
    $draggingOverNonDroppable: boolean;
}>`
    cursor: pointer;
    border-radius: calc(${vars.radius.xl} - 8px);
    user-select: none;

    ${getKanbanCardStyles}
`;

export const KanbanCardHoverAction = styled.div`
    visibility: hidden;

    ${Card}:hover &,
    ${Card}:focus-within &,
    ${Card}:focus-visible & {
        visibility: visible;
    }

    /* Keep menu button visible when menu is open */
    &:has(button[data-state='open']) {
        visibility: visible;
    }

    /* Keep all hover actions visible when menu is open */
    [data-menu-open='true'] & {
        visibility: visible;
    }
`;
